import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'

import usePopup, {
  PopupReturnType,
  PopupReturnTypeWithPayload
} from 'hooks/usePopup'
import { DeleteResponseUserPayload } from 'features/RFP/RFPDetails/ResponsibleForRFP/DeleteUserPopup/types'
import { initialPopup } from '../../../../hooks/constants'
import { defaultDeleteResponsibleUserPopup } from './constants'

type ContextProps = {
  sendRFPPopup: PopupReturnType
  sendRFPToCommonsPopup: PopupReturnType
  cancelRFPPopup: PopupReturnType
  changeRFPResponsePopup: PopupReturnType
  createSurveyPopup: PopupReturnType
  awardVendorsPopup: PopupReturnType
  submitSurveyPopup: PopupReturnTypeWithPayload<{
    stakeholderId?: string
    categoryId?: string
  }>
  addResponsibleUserPopup: PopupReturnType
  addResponsibleCommonsUserPopup: PopupReturnType
  resendBidPopup: PopupReturnType
  submitConfirmPopup: PopupReturnType
  rankingUnchangedPopup: PopupReturnType
  deleteResponsibleUserPopup: PopupReturnTypeWithPayload<DeleteResponseUserPayload>
  addHealthSystemPopup: PopupReturnType
  deleteHealthSystemsPopup: PopupReturnType
}

const RFPDetailsPopupContext = createContext<ContextProps>({
  sendRFPPopup: initialPopup,
  sendRFPToCommonsPopup: initialPopup,
  cancelRFPPopup: initialPopup,
  changeRFPResponsePopup: initialPopup,
  createSurveyPopup: initialPopup,
  awardVendorsPopup: initialPopup,
  submitSurveyPopup: initialPopup as PopupReturnTypeWithPayload<{
    stakeholderId?: string
    categoryId?: string
  }>,
  addResponsibleUserPopup: initialPopup,
  addResponsibleCommonsUserPopup: initialPopup,
  resendBidPopup: initialPopup,
  submitConfirmPopup: initialPopup,
  rankingUnchangedPopup: initialPopup,
  deleteResponsibleUserPopup: defaultDeleteResponsibleUserPopup,
  addHealthSystemPopup: initialPopup,
  deleteHealthSystemsPopup: initialPopup
})

const basePopup = (title: string) => ({
  title
})

const RFPDetailsPopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const changeRFPResponsePopupProps = usePopup(
    basePopup('Change RFP response?')
  )
  const sendRFPPopupProps = usePopup(basePopup('Bidding steps time'))
  const sendRFPToCommonsPopupProps = usePopup(
    basePopup('Send RFP request to OM Lifeline')
  )
  const cancelRFPPopupProps = usePopup(basePopup('Cancel'))
  const createSurveyPopupProps = usePopup(basePopup('Create survey'))
  const awardVendorsPopupProps = usePopup(basePopup('Vendor selection'))
  const submitSurveyPopupProps = usePopup<{
    stakeholderId?: string
    categoryId?: string
  }>(basePopup('survey'))
  const resendBidPopupProps = usePopup(basePopup('Resend Bid?'))
  const submitConfirmProps = usePopup(basePopup('Submit survey response?'))
  const rankingUnchangedProps = usePopup(basePopup('Default Ranking Unchanged'))
  const addResponsibleUserPopupProps = usePopup(
    basePopup('Add team members to the RFP')
  )
  const addResponsibleCommonsUserPopupProps = usePopup(
    basePopup('Add responsible for RFP')
  )

  const deleteResponsibleUserPopupProps = usePopup<DeleteResponseUserPayload>(
    basePopup('Delete team member?')
  )
  const addHealthSystemPopupProps = usePopup(basePopup('Add Health System'))
  const deleteHealthSystemsPopupProps = usePopup(
    basePopup('Delete Health System')
  )

  const context = useMemo(
    () => ({
      sendRFPPopup: sendRFPPopupProps,
      cancelRFPPopup: cancelRFPPopupProps,
      changeRFPResponsePopup: changeRFPResponsePopupProps,
      createSurveyPopup: createSurveyPopupProps,
      awardVendorsPopup: awardVendorsPopupProps,
      submitSurveyPopup: submitSurveyPopupProps,
      addResponsibleUserPopup: addResponsibleUserPopupProps,
      deleteResponsibleUserPopup: deleteResponsibleUserPopupProps,
      sendRFPToCommonsPopup: sendRFPToCommonsPopupProps,
      resendBidPopup: resendBidPopupProps,
      submitConfirmPopup: submitConfirmProps,
      rankingUnchangedPopup: rankingUnchangedProps,
      addResponsibleCommonsUserPopup: addResponsibleCommonsUserPopupProps,
      addHealthSystemPopup: addHealthSystemPopupProps,
      deleteHealthSystemsPopup: deleteHealthSystemsPopupProps
    }),
    [
      sendRFPPopupProps,
      cancelRFPPopupProps,
      changeRFPResponsePopupProps,
      createSurveyPopupProps,
      awardVendorsPopupProps,
      submitSurveyPopupProps,
      addResponsibleUserPopupProps,
      deleteResponsibleUserPopupProps,
      sendRFPToCommonsPopupProps,
      resendBidPopupProps,
      submitConfirmProps,
      rankingUnchangedProps,
      addResponsibleCommonsUserPopupProps,
      addHealthSystemPopupProps,
      deleteHealthSystemsPopupProps
    ]
  )

  return (
    <RFPDetailsPopupContext.Provider value={context}>
      {children}
    </RFPDetailsPopupContext.Provider>
  )
}

export const useRFPDetailsPopupContext = () =>
  useContext(RFPDetailsPopupContext)

export default RFPDetailsPopupProvider
